import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2, Package, Truck, ArrowRight, ArrowLeft, User, Phone, Shield } from 'lucide-react';
import { trackBite, trackOTPSent } from '../components/Analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetchCompat as fetch } from '../utils/safeFetch';

const PricingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: choose service, 1: name/mobile, 2: OTP
  const [serviceType, setServiceType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    otp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [leadToken, setLeadToken] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceSelect = (type) => {
    // Track the "bite" - user clicked to request pricing
    trackBite('request_pricing_click', '/pricing', type);
    setServiceType(type);
    setStep(1);
  };

  const handleSendOTP = async () => {
    setIsSubmitting(true);
    setError('');
    
    // Track OTP sent for follow-up
    trackOTPSent(formData.mobile, 'pricing_inquiry', formData.name, null, serviceType);
    
    try {
      const response = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formData.mobile })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStep(2);
      } else {
        setError(data.detail || data.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleVerifyOTP = async () => {
    setIsVerifying(true);
    setError('');
    
    try {
      // First verify the OTP
      const otpResponse = await fetch(`${API_URL}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone_number: formData.mobile,
          code: formData.otp 
        })
      });
      
      const otpData = await otpResponse.json();
      
      if (!otpResponse.ok || !otpData.valid) {
        setError(otpData.message || 'Invalid verification code. Please try again.');
        setIsVerifying(false);
        return;
      }
      
      // OTP verified, now submit the lead
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_interest: serviceType,
          name: formData.name,
          mobile: formData.mobile
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      const data = await response.json();
      setLeadToken(data.token);
      
      // Redirect to personalized pricing page
      navigate(`/quote/${data.token}`);
    } catch (err) {
      setError('Failed to submit inquiry. Please try again.');
    }
    
    setIsVerifying(false);
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <main className="bg-[#F8FAFC] min-h-screen">
      {/* Header */}
      <section className="bg-white border-b border-[#E2E8F0] py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111]">
            {step === 0 ? 'Get Pricing Information' : 'Request Pricing'}
          </h1>
          <p className="text-[#64748B] mt-3">
            {step === 0 ? "Tell us about the service you're interested in" : "We'll show you tailored pricing"}
          </p>
          {step > 0 && (
            <div className="mt-4 flex justify-center gap-2">
              {[1, 2].map((s) => (
                <div 
                  key={s} 
                  className={`w-10 h-1 rounded-full transition-colors ${
                    s <= step ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Step 0: Choose Service Type */}
          {step === 0 && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-center text-[#64748B] mb-6">
                What service are you interested in?
              </p>
              
              <Card 
                className="border-[#E2E8F0] cursor-pointer card-hover hover:border-[#0D9488] transition-colors"
                onClick={() => handleServiceSelect('pickup')}
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center">
                    <Package className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#111111]">Collect Packaging</h3>
                    <p className="text-sm text-[#64748B]">We collect and dispose of packaging waste from job sites</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                </CardContent>
              </Card>

              <Card 
                className="border-[#E2E8F0] cursor-pointer card-hover hover:border-[#0D9488] transition-colors"
                onClick={() => handleServiceSelect('delivery')}
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center">
                    <Truck className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#111111]">Delivery Service</h3>
                    <p className="text-sm text-[#64748B]">Equipment delivery to your job sites</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                </CardContent>
              </Card>

              <p className="text-center text-sm text-[#64748B] mt-6">
                We serve Metro Melbourne only
              </p>
            </div>
          )}

          {/* Step 1: Name and Mobile */}
          {step === 1 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <User className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Your Contact Details</h2>
                  <p className="text-sm text-[#64748B] mt-1">We'll use this to send you pricing information</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Your Name</label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Mobile Number</label>
                    <Input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      placeholder="04XX XXX XXX"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12"
                    />
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={handleSendOTP}
                    disabled={!formData.name || !formData.mobile || isSubmitting}
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending OTP...</>
                    ) : (
                      <>Next <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <Card className="border-[#E2E8F0] animate-fade-in">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#111111]">Verify Your Number</h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    We've sent a verification code to <strong>{formData.mobile}</strong>
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Enter OTP Code</label>
                    <Input
                      type="text"
                      value={formData.otp}
                      onChange={(e) => handleChange('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="border-[#E2E8F0] focus:border-[#0D9488] h-12 text-center text-2xl tracking-widest"
                      maxLength={6}
                    />
                  </div>
                  <p className="text-center text-sm text-[#64748B]">
                    Didn't receive it? <button className="text-[#0D9488] hover:underline">Resend OTP</button>
                  </p>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={goBack}
                    className="border-[#E2E8F0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={handleVerifyOTP}
                    disabled={formData.otp.length < 4 || isVerifying}
                  >
                    {isVerifying ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
                    ) : (
                      <>Submit Request <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </section>
    </main>
  );
};

export default PricingPage;
